import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { FindExtension } from './tiptap-find-extension'
import { TiptapFindBar } from './tiptap-find-bar'
import { TiptapFormattingToolbar } from './tiptap-formatting-toolbar'
import { Image } from '@tiptap/extension-image'
import {
  isAttachmentApiPath,
  loadAuthenticatedImageSrc,
  revokeAuthenticatedBlobUrl,
} from '@/lib/authenticated-attachment-image'
import { resolveMarkdownImageSrc } from '@/lib/resolve-signed-url'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import { TaskList } from '@tiptap/extension-task-list'
import { Typography } from '@tiptap/extension-typography'
import { Link } from '@tiptap/extension-link'
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { marked } from 'marked'
import { Loader2 } from 'lucide-react'
import { fetchWithAuth } from '@/lib/api'
import './document-reading-prose.css'
import './tiptap-styles.css'
import { devLog } from '@/lib/dev-log'
import {
  MermaidCodeBlock,
  CustomKeyboardExtension,
  HeadingWithId,
  BlockIdExtension,
  TableSafeTaskItem,
  setOpenFindBarCallback,
} from './tiptap-editor.extensions'
import {
  type BlockLineMap,
  createLineMap,
  injectBlockIds,
  stripFrontmatter,
  convertMarkedTaskListsToTipTap,
  editorHtmlToMarkdown,
  protectFootnoteDefinitions,
  focusTipTapEditor,
  type ViewportFocusCoords,
} from './tiptap-editor.utils'
import { useTipTapEditorHandlers } from './tiptap-editor.handlers'
import { useTipTapCopyHandler } from './tiptap-editor.copy-handler'
import { hrefForInAppDocumentNavigation } from '@/lib/maater-document-url'

interface TipTapEditorProps {
  initialContent?: string
  onChange?: (content: string, options?: { immediate?: boolean; syncOnly?: boolean }) => void
  readOnly?: boolean
  darkMode?: boolean
  className?: string
  onInternalLinkClick?: (href: string, event?: MouseEvent) => void
  documentId?: string // Required for image uploads and copy-to-chip
  documentTitle?: string // Human-readable title for the chip
  documentPath?: string // Relative file path for the chip tooltip
  onCopyWithReference?: (data: { text: string; lineStart?: number; lineEnd?: number; isFullLine: boolean }) => void
  onFocus?: () => void
  onBlur?: () => void
  /** `default` = ~760px reading column; `full` = prose uses full panel width */
  pageWidthMode?: "default" | "full"
  /** When `external`, parent provides scroll; TipTap content expands naturally. */
  scrollContainer?: "internal" | "external"
  /** Parent registers a flush fn to sync TipTap HTML to markdown before preview/save. */
  wysiwygFlushRef?: React.MutableRefObject<(() => string) | null>
  /** When true, focus the editor once after mount (new doc or preview double-click). */
  autoFocusOnMount?: boolean
  /** Viewport coords from a preview double-click; caret is placed at the hit position when possible. */
  focusCoords?: ViewportFocusCoords | null
  /** Scroll offset captured before preview->edit; reapplied after focus so the viewport does not jump. */
  focusPreserveScrollTop?: number | null
  /** Called after auto-focus runs so parents can clear one-shot focus state. */
  onAutoFocusComplete?: () => void
  /** One-shot: open find bar after TipTap mounts (e.g. Cmd+F from Visual preview). */
  openFindOnMount?: boolean
  /** Clears parent one-shot open-find flag after the bar opens. */
  onOpenFindOnMountConsumed?: () => void
}

export function TipTapEditor({
  initialContent = "",
  onChange,
  readOnly = false,
  darkMode = false,
  onInternalLinkClick,
  documentId,
  documentTitle,
  documentPath,
  onCopyWithReference,
  onFocus,
  onBlur,
  pageWidthMode = "default",
  scrollContainer = "internal",
  wysiwygFlushRef,
  autoFocusOnMount = false,
  focusCoords = null,
  focusPreserveScrollTop = null,
  onAutoFocusComplete,
  openFindOnMount = false,
  onOpenFindOnMountConsumed,
}: TipTapEditorProps) {
  // Track if user is actively editing to prevent cursor jumps
  const isUserEditingRef = useRef(false)
  const lastUserEditTimeRef = useRef(0)
  const onInternalLinkClickRef = useRef(onInternalLinkClick)
  onInternalLinkClickRef.current = onInternalLinkClick
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [findOpen, setFindOpen] = useState(false)
  const [findFocusRequestId, setFindFocusRequestId] = useState(0)
  const autoFocusAppliedRef = useRef(false)
  const openFindConsumedRef = useRef(false)

  const editorRootRef = useRef<HTMLDivElement>(null)

  const openOrFocusFind = useCallback(() => {
    setFindOpen(true)
    setFindFocusRequestId((n) => n + 1)
  }, [])

  // Wire the module-level callback so the static CustomKeyboardExtension can open the find bar
  useEffect(() => {
    setOpenFindBarCallback(openOrFocusFind)
    return () => { setOpenFindBarCallback(null) }
  }, [openOrFocusFind])

  // Cmd+F while TipTap is mounted (even if focus is outside the prose surface).
  // If find is already open, re-focus the query and select it so the keyword is editable.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'f' || e.shiftKey || e.altKey) {
        return
      }
      const target = e.target as HTMLElement | null
      if (!target) return
      const inFindBar = Boolean(target.closest('[data-testid="tiptap-find-bar"]'))
      const inForeignField =
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable) &&
        !target.closest('.tiptap-editor') &&
        !inFindBar
      if (inForeignField) return
      // Inactive document tabs stay mounted (display:none). Do not steal Cmd+F
      // from the visible index (Inbox, folder list).
      if (!editorRootRef.current || editorRootRef.current.getClientRects().length === 0) return

      e.preventDefault()
      e.stopPropagation()
      openOrFocusFind()
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [openOrFocusFind])

  // Shadow line map: tracks block IDs to source line numbers
  const lineMapRef = useRef<BlockLineMap>({})

  // Convert markdown to HTML for TipTap
  const htmlContent = useMemo(() => {
    if (!initialContent) {
      lineMapRef.current = {}
      return ''
    }

    let contentToRender = initialContent

    // Strip frontmatter if present (should be handled by backend, but just in case)
    if (contentToRender.trim().startsWith('---')) {
      contentToRender = stripFrontmatter(contentToRender)
    }

    // If content already looks like HTML, use it as is
    if (contentToRender.trim().startsWith('<')) {
      lineMapRef.current = {}
      return contentToRender
    }

    // Create line map BEFORE processing (tracks original markdown structure)
    const previousBlockCount = Object.keys(lineMapRef.current).length
    lineMapRef.current = createLineMap(contentToRender)
    const newBlockCount = Object.keys(lineMapRef.current).length

    // Only log when block count changes significantly (reduces console noise)
    // This prevents logging on every keystroke - only log when structure changes meaningfully
    const blockCountChanged = Math.abs(newBlockCount - previousBlockCount) > 10
    if (blockCountChanged && previousBlockCount > 0) {
      // Only log when transitioning from non-empty to different structure
      devLog('[TipTapEditor] Line map updated (structure changed):', {
        blockCount: newBlockCount,
        previousBlockCount,
        change: newBlockCount - previousBlockCount
      })
    }

    // Otherwise, parse markdown to HTML (with GFM support for tables)
    // Legacy: older saves used markdown backslash hard breaks (\\\n). Normalize to GFM
    // two-space breaks so marked does not treat "\\" lines as content (which caused "\\"-only
    // lines and runaway <br class="hard-break"> when breaks:true was enabled).
    contentToRender = contentToRender.replace(/\\\s*\n/g, '  \n')

    // Footnote definitions (`[^1]: Ibid.`) look like link reference definitions to marked,
    // which would swallow short ones and drop them on the next save. Escaping the opening
    // bracket makes marked emit the literal text; the serializer restores `[^n]` on save.
    contentToRender = protectFootnoteDefinitions(contentToRender)

    let html = marked.parse(contentToRender, {
      async: false,
      gfm: true,  // Enable GitHub Flavored Markdown (tables, strikethrough, etc.)
      // false: single newlines stay as word-wrap inside a paragraph; hard breaks use "  \n"
      breaks: false,
    }) as string

    // Chat links use doc:uuid. TipTap's link mark keeps the href, but a click
    // needs a path the document opener already understands.
    html = html.replace(
      /href=(["'])doc:([0-9a-f-]{36})\1/gi,
      'href=$1/doc/$2$1',
    )

    // marked task lists use bare checkbox inputs; TipTap requires data-type attributes
    html = convertMarkedTaskListsToTipTap(html)

    // Post-process: Split paragraphs that contain multiple consecutive bold statements
    // This fixes the case where marked.parse() merges consecutive bold paragraphs
    // Pattern: <p>...<strong>...</strong>...\n<strong>...</strong>...</p>
    html = html.replace(
      /<p>([\s\S]*?(?:<strong>[^<]+?<\/strong>[^<]*?)\s*\n\s*(?:<strong>[^<]+?<\/strong>[^<]*?)+[\s\S]*?)<\/p>/g,
      (match, content) => {
        // Split on newline followed by <strong> (separates bold statements)
        const boldStatements = content.split(/\n\s*(<strong>)/)

        if (boldStatements.length > 2) {
          // Reconstruct as separate paragraphs
          let result = '<p>' + boldStatements[0]

          for (let i = 1; i < boldStatements.length; i += 2) {
            result += '</p><p>' + boldStatements[i]
            if (boldStatements[i + 1]) {
              result += boldStatements[i + 1]
            }
          }

          result += '</p>'
          return result
        }
        return match
      }
    )

    // Inject block IDs into HTML
    html = injectBlockIds(html, lineMapRef.current)

    return html
  }, [initialContent])

  // Create refs for paste handler to access state and editor
  const editorRef = useRef<any>(null)
  const setUploadingRef = useRef(setUploading)
  const setUploadErrorRef = useRef(setUploadError)

  // Track if we're doing a programmatic update to prevent onChange loops
  const isProgrammaticUpdateRef = useRef(false)
  const lastEmittedContentRef = useRef<string>(initialContent)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const flushEditorToMarkdown = (): string => {
    if (!editorRef.current || readOnly) {
      return lastEmittedContentRef.current
    }

    const markdown = editorHtmlToMarkdown(editorRef.current.getHTML())
    if (markdown !== lastEmittedContentRef.current) {
      lastEmittedContentRef.current = markdown
      onChangeRef.current?.(markdown, { syncOnly: true })
    }
    return markdown
  }

  // Update refs when state setters change
  useEffect(() => {
    setUploadingRef.current = setUploading
    setUploadErrorRef.current = setUploadError
  }, [])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        hardBreak: {
          keepMarks: false,
          HTMLAttributes: {
            class: 'hard-break',
          },
        },
        heading: false,
        codeBlock: false, // replaced by MermaidCodeBlock below
      }),
      MermaidCodeBlock.configure({
        HTMLAttributes: { class: 'code-block' },
      }),
      HeadingWithId.configure({ levels: [1, 2, 3, 4, 5, 6] }),
      Link.configure({
        openOnClick: false,     // let the existing handleClick manage navigation
        autolink: false,        // don't auto-detect URLs while typing
        // Override isAllowedUri to explicitly permit relative paths (e.g. todos/filename.md)
        // Default only allows http/https/ftp/mailto/tel/etc. protocols
        isAllowedUri: (url, ctx) => {
          // Block javascript: URIs
          if (url.startsWith('javascript:')) return false
          // Allow relative paths (no protocol prefix)
          if (!url.includes('://') && !url.startsWith('mailto:') && !url.startsWith('tel:')) return true
          // Delegate absolute URLs to default validator
          return ctx.defaultValidate(url)
        },
        HTMLAttributes: {
          class: 'text-blue-500 underline hover:text-blue-700',
          rel: null,            // don't force rel="noopener" on internal links
          target: null,         // don't force target="_blank" on internal links
        },
      }),
      Image.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            width: {
              default: null,
              renderHTML: attributes => {
                if (!attributes.width) {
                  return {}
                }
                return {
                  width: attributes.width,
                }
              },
              parseHTML: element => element.getAttribute('width'),
            },
            height: {
              default: null,
              renderHTML: attributes => {
                if (!attributes.height) {
                  return {}
                }
                return {
                  height: attributes.height,
                }
              },
              parseHTML: element => element.getAttribute('height'),
            },
          }
        },
        addNodeView() {
          return ({ node, HTMLAttributes, getPos, editor }) => {
            const dom = document.createElement('div')
            dom.className = 'image-wrapper'

            const img = document.createElement('img')
            Object.entries(HTMLAttributes).forEach(([key, value]) => {
              if (key !== 'class') {
                img.setAttribute(key, value)
              }
            })
            const rawSrc = String(node.attrs.src || '')
            const resolvedSrc = resolveMarkdownImageSrc(rawSrc)
            img.alt = node.attrs.alt || ''
            // Placeholder until auth fetch completes (attachment paths need Bearer).
            img.src = isAttachmentApiPath(resolvedSrc) ? '' : resolvedSrc

            let activeBlobUrl: string | null = null
            let cancelled = false

            if (isAttachmentApiPath(resolvedSrc)) {
              loadAuthenticatedImageSrc(resolvedSrc)
                .then((url) => {
                  if (cancelled) {
                    revokeAuthenticatedBlobUrl(url)
                    return
                  }
                  if (url.startsWith('blob:')) activeBlobUrl = url
                  img.src = url
                })
                .catch(() => {
                  if (!cancelled) {
                    img.alt = img.alt || 'Image unavailable'
                  }
                })
            }

            // Set initial dimensions if provided
            if (node.attrs.width) {
              img.style.width = `${node.attrs.width}px`
            }
            if (node.attrs.height) {
              img.style.height = `${node.attrs.height}px`
            }

            dom.appendChild(img)

            // Add resize handle
            const handle = document.createElement('div')
            handle.className = 'image-resize-handle'

            let isResizing = false
            let startX = 0
            let startWidth = 0
            let startHeight = 0
            let aspectRatio = 1

            // Calculate aspect ratio from natural image size
            img.onload = () => {
              aspectRatio = img.naturalWidth / img.naturalHeight
            }

            // Recover signed URLs that fail at load time for any reason (e.g. a
            // non-expired token that gets revoked or a network hiccup). Expired
            // tokens are already handled above at NodeView construction time, so
            // this path is rare — just route through the resolve endpoint once.
            img.onerror = () => {
              const src = img.src
              const signMarker = '/storage/v1/object/sign/flownotes-assets/'
              if (!src.includes(signMarker)) return
              if (img.dataset.resolveAttempted) return

              const afterBucket = src.split(signMarker)[1]
              const storagePath = afterBucket ? afterBucket.split('?')[0] : null
              if (storagePath) {
                img.dataset.resolveAttempted = '1'
                const resolvePath = `/api/attachments/resolve?path=${encodeURIComponent(storagePath)}`
                loadAuthenticatedImageSrc(resolvePath)
                  .then((url) => {
                    if (cancelled) {
                      revokeAuthenticatedBlobUrl(url)
                      return
                    }
                    if (url.startsWith('blob:')) {
                      revokeAuthenticatedBlobUrl(activeBlobUrl)
                      activeBlobUrl = url
                    }
                    img.src = url
                  })
                  .catch(() => {
                    /* leave broken */
                  })
              }
            }

            handle.addEventListener('mousedown', (e) => {
              e.preventDefault()
              e.stopPropagation()
              isResizing = true
              startX = e.clientX
              startWidth = img.offsetWidth || img.naturalWidth
              startHeight = img.offsetHeight || img.naturalHeight
              aspectRatio = startWidth / startHeight

              document.addEventListener('mousemove', handleMouseMove)
              document.addEventListener('mouseup', handleMouseUp)
            })

            const handleMouseMove = (e: MouseEvent) => {
              if (!isResizing) return

              const deltaX = e.clientX - startX
              const newWidth = Math.max(50, Math.min(2000, startWidth + deltaX))
              const newHeight = newWidth / aspectRatio

              img.style.width = `${newWidth}px`
              img.style.height = `${newHeight}px`
            }

            const handleMouseUp = () => {
              if (!isResizing) return
              isResizing = false

              // Update node attributes
              const pos = getPos()
              if (typeof pos === 'number') {
                const newWidth = Math.round(parseFloat(img.style.width))

                editor.chain().focus().command(({ tr }) => {
                  // Height stays auto so max-width scaling keeps the aspect ratio.
                  const attrs = {
                    ...node.attrs,
                    width: newWidth,
                    height: null,
                  }
                  tr.setNodeMarkup(pos, undefined, attrs)
                  return true
                }).run()
              }

              document.removeEventListener('mousemove', handleMouseMove)
              document.removeEventListener('mouseup', handleMouseUp)
            }

            dom.appendChild(handle)

            return {
              dom,
              contentDOM: null,
              destroy() {
                cancelled = true
                revokeAuthenticatedBlobUrl(activeBlobUrl)
                activeBlobUrl = null
              },
            }
          }
        },
      }).configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'tiptap-image',
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
      TaskList.configure({
        HTMLAttributes: {
          class: 'task-list',
        },
      }),
      TableSafeTaskItem.configure({
        nested: true,
        HTMLAttributes: {
          class: 'task-item',
        },
      }),
      Typography,
      CustomKeyboardExtension,
      BlockIdExtension, // Preserve data-block-id attributes for line number mapping
      FindExtension,
    ],
    content: htmlContent,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      // Skip if this is a programmatic update (not user input)
      if (isProgrammaticUpdateRef.current) {
        return
      }

      // Mark that user is actively editing
      isUserEditingRef.current = true
      lastUserEditTimeRef.current = Date.now()

      const markdown = editorHtmlToMarkdown(editor.getHTML())

      // Only call onChange if content actually changed (prevent loops)
      if (markdown !== lastEmittedContentRef.current) {
        lastEmittedContentRef.current = markdown
        onChange?.(markdown)
      }
    },
    editorProps: {
      attributes: {
        class: `tiptap-editor focus:outline-none ${darkMode ? 'dark-mode' : ''}`,
      },
      handleClick: (_view, _pos, event) => {
        const target = event.target as HTMLElement
        const anchor = target.closest('a')
        if (anchor) {
          const href = anchor.getAttribute('href')
          const inAppHref = href ? hrefForInAppDocumentNavigation(href) : null
          if (inAppHref) {
            event.preventDefault()
            // Pass the DOM event so Cmd/Ctrl-click opens a new Maater tab (ADR-0016)
            onInternalLinkClickRef.current?.(inAppHref, event as unknown as MouseEvent)
            return true
          }
          if (href && /^https?:\/\//i.test(href)) {
            event.preventDefault()
            window.open(href, '_blank')
            return true
          }
        }
        return false
      },
      handlePaste: (view, event, _slice) => {
        // Early return for read-only mode - let TipTap handle normally
        if (readOnly) {
          return false
        }

        const items = Array.from(event.clipboardData?.items || [])
        const imageItem = items.find(item => item.type.startsWith('image/'))

        // Only intercept image pastes (requires documentId)
        if (imageItem && documentId) {
          event.preventDefault()
          const file = imageItem.getAsFile()
          if (file) {
            // Handle async upload without blocking
            ;(async () => {
              setUploadingRef.current(true)
              setUploadErrorRef.current(null)

              try {
                const formData = new FormData()
                formData.append('file', file)
                formData.append('documentId', documentId)

                const response = await fetchWithAuth('/api/attachments/upload', {
                  method: 'POST',
                  body: formData,
                })

                if (!response.ok) {
                  // Handle 413 and other errors that might return HTML
                  let error
                  const contentType = response.headers.get('content-type')
                  if (contentType && contentType.includes('application/json')) {
                    error = await response.json()
                  } else {
                    // HTML error page (like 413 from nginx)
                    const text = await response.text()
                    if (response.status === 413) {
                      error = {
                        error: 'File too large',
                        details: `File size exceeds the maximum allowed size (10MB). Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`
                      }
                    } else {
                      error = { error: `Upload failed (${response.status})`, details: text.substring(0, 200) }
                    }
                  }
                  throw new Error(error.details || error.error || 'Upload failed')
                }

                const result = await response.json()

                if (result.success && result.attachment) {
                  const markdownHtml = marked.parse(result.attachment.markdown) as string
                  // Use editor instance to insert content
                  if (editorRef.current) {
                    editorRef.current.commands.insertContent(markdownHtml)
                  } else {
                    // Fallback: insert as HTML using view
                    const pos = view.state.selection.$from.pos
                    view.dispatch(view.state.tr.insertText(result.attachment.markdown, pos))
                  }
                  setUploadingRef.current(false)
                } else {
                  throw new Error('Invalid response from server')
                }
              } catch (error: any) {
                console.error('Image upload error:', error)
                setUploadErrorRef.current(error.message || 'Failed to upload image')
                setUploadingRef.current(false)
              }
            })()
          }
          return true
        }

        // For all other pastes (text, HTML, etc.), manually insert content
        // Note: Returning false doesn't properly restore TipTap's default handler,
        // so we need to handle text/HTML pastes ourselves
        // devLog('[TipTap Paste Handler] Handling text/HTML paste manually')

        // Get the clipboard data
        const clipboardData = event.clipboardData
        if (!clipboardData) {
          // devLog('[TipTap Paste Handler] No clipboard data available')
          return false
        }

        // Try to get HTML first (preserves formatting)
        let pasteContent = clipboardData.getData('text/html')

        // If no HTML, fall back to plain text
        if (!pasteContent || pasteContent.trim() === '') {
          pasteContent = clipboardData.getData('text/plain')
          if (pasteContent) {
            // Convert plain text to HTML paragraphs
            pasteContent = pasteContent.split('\n').map((line: string) => {
              if (line.trim() === '') return '<p><br></p>'
              return `<p>${line.trim()}</p>`
            }).join('')
          }
        }

        if (pasteContent && editorRef.current) {
          // devLog('[TipTap Paste Handler] Inserting content into editor')
          event.preventDefault()

          // Clean up the HTML before inserting (strip unwanted styles/attributes)
          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = pasteContent

          // Strip inline styles and unwanted attributes
          const cleanNodes = tempDiv.querySelectorAll('*')
          cleanNodes.forEach(node => {
            if (node instanceof HTMLElement) {
              // Keep basic formatting but remove styles
              node.removeAttribute('style')
              node.removeAttribute('class')
              // Remove data attributes that might cause issues
              Array.from(node.attributes).forEach(attr => {
                if (attr.name.startsWith('data-') && !attr.name.startsWith('data-blank-line')) {
                  node.removeAttribute(attr.name)
                }
              })
            }
          })

          const cleanedHtml = tempDiv.innerHTML

          // Insert the cleaned content
          editorRef.current.commands.insertContent(cleanedHtml)
          // devLog('[TipTap Paste Handler] Content inserted successfully')
          return true
        }

        // devLog('[TipTap Paste Handler] No content to paste or editor not available')
        return false
      },
    },
  })

  // Only update content when it's actually different (not from user typing)
  // This prevents cursor jumping
  useEffect(() => {
    if (!editor || !htmlContent) return

    // Don't update if user just edited (within last 500ms)
    // This prevents cursor jumps during typing
    const timeSinceLastEdit = Date.now() - lastUserEditTimeRef.current
    if (isUserEditingRef.current && timeSinceLastEdit < 500) {
      return
    }

    // Convert current editor content to markdown to compare
    const currentMarkdown = editorHtmlToMarkdown(editor.getHTML()).trim()
    const incomingMarkdown = initialContent.trim()

    // Only update if the markdown content is actually different
    // This prevents resetting the editor while user is typing
    if (currentMarkdown !== incomingMarkdown) {
      // Reset editing flag before updating
      isUserEditingRef.current = false
      // Update last emitted content to match incoming content (prevents loop)
      lastEmittedContentRef.current = incomingMarkdown
      // Mark as programmatic update to prevent onChange loop
      isProgrammaticUpdateRef.current = true
      editor.commands.setContent(htmlContent)
      // Clear flag after a microtask to allow update to complete
      Promise.resolve().then(() => {
        isProgrammaticUpdateRef.current = false
      })
    }
  }, [htmlContent, editor, initialContent])

  // Use copy handler hook
  useTipTapCopyHandler({
    editor,
    documentId,
    documentTitle,
    documentPath,
    lineMapRef,
    initialContent,
    onCopyWithReference,
  })

  // Update editable state and store editor ref
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly)
      editorRef.current = editor

      // Add focus/blur listeners
      if (onFocus || onBlur) {
        const focusHandler = () => {
          onFocus?.()
        }
        const blurHandler = () => {
          onBlur?.()
        }
        editor.on('focus', focusHandler)
        editor.on('blur', blurHandler)

        return () => {
          editor.off('focus', focusHandler)
          editor.off('blur', blurHandler)
        }
      }
    }
  }, [readOnly, editor, onFocus, onBlur])

  useEffect(() => {
    autoFocusAppliedRef.current = false
    openFindConsumedRef.current = false
  }, [documentId])

  useEffect(() => {
    if (!openFindOnMount) {
      openFindConsumedRef.current = false
      return
    }
    if (!editor || openFindConsumedRef.current) return
    openFindConsumedRef.current = true
    openOrFocusFind()
    onOpenFindOnMountConsumed?.()
  }, [editor, openFindOnMount, onOpenFindOnMountConsumed, openOrFocusFind])

  useEffect(() => {
    // Prefer find-bar focus when opening from preview Cmd+F
    if (!editor || readOnly || !autoFocusOnMount || openFindOnMount) return
    if (autoFocusAppliedRef.current) return
    autoFocusAppliedRef.current = true
    const scrollParent =
      editor.view.dom.closest('[data-scroll-container]') as HTMLElement | null
    const preserveScrollTop =
      focusPreserveScrollTop ?? scrollParent?.scrollTop ?? null
    focusTipTapEditor(editor, focusCoords, { scrollParent, preserveScrollTop })
    onAutoFocusComplete?.()
  }, [
    editor,
    readOnly,
    autoFocusOnMount,
    openFindOnMount,
    focusCoords,
    focusPreserveScrollTop,
    onAutoFocusComplete,
  ])

  // Use handlers hook for uploadImage and handleDrop
  const { handleDrop } = useTipTapEditorHandlers({
    fetchWithAuth,
    setUploading,
    setUploadError,
    editor: editorRef.current,
    documentId,
    readOnly,
    marked,
  })


  // Save on blur (editor loses focus)
  useEffect(() => {
    if (!editor || readOnly) return undefined

    const handleBlur = () => {
      if (onChange) {
        const markdown = editorHtmlToMarkdown(editor.getHTML())
        if (markdown !== lastEmittedContentRef.current) {
          lastEmittedContentRef.current = markdown
          onChange(markdown, { immediate: true })
        }
      }
    }

    editor.on('blur', handleBlur)
    return () => {
      editor.off('blur', handleBlur)
    }
  }, [editor, onChange, readOnly])

  // Expose flush to parent (preview toggle, manual save) and sync on unmount
  useEffect(() => {
    if (!wysiwygFlushRef) return undefined

    wysiwygFlushRef.current = flushEditorToMarkdown
    return () => {
      wysiwygFlushRef.current = null
    }
  }, [editor, readOnly, wysiwygFlushRef])

  useEffect(() => {
    return () => {
      flushEditorToMarkdown()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!editor) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <div className="text-center">
          <div className="animate-pulse">Loading editor...</div>
          <div className="text-xs mt-2">Initializing TipTap</div>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={editorRootRef}
      className={`tiptap-editor print-visible bg-background relative ${darkMode ? 'dark-mode' : ''} ${
        scrollContainer === 'external' ? 'tiptap-editor--external-scroll' : 'tiptap-editor--internal-scroll'
      }`}
      style={
        scrollContainer === 'external'
          ? ({
              "--prose-max-width": pageWidthMode === "full" ? "none" : "760px",
            } as CSSProperties)
          : ({
              height: "100%",
              maxHeight: "100%",
              "--prose-max-width": pageWidthMode === "full" ? "none" : "760px",
            } as CSSProperties)
      }
      onDrop={handleDrop}
      onDragOver={(e) => {
        if (!readOnly && documentId) {
          e.preventDefault()
        }
      }}
    >
      {uploading && (
        <div className="absolute top-4 right-4 z-50 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Uploading image...</span>
        </div>
      )}
      {uploadError && (
        <div className="absolute top-4 right-4 z-50 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg">
          {uploadError}
          <button
            onClick={() => setUploadError(null)}
            className="ml-2 underline"
          >
            Dismiss
          </button>
        </div>
      )}
      {findOpen && editor && (
        <TiptapFindBar
          editor={editor}
          onClose={() => setFindOpen(false)}
          focusRequestId={findFocusRequestId}
        />
      )}
      {!readOnly && editor && <TiptapFormattingToolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  )
}

